// @flow

import { ThemeProvider } from '@emotion/react'
import styled from '@emotion/styled'
import { Link, graphql, withPrefix } from 'gatsby'
import { StaticImage } from 'gatsby-plugin-image'
import * as React from 'react'

import { colors, fonts, media, themes } from '@toggl/style'
import {
  Brands,
  Button,
  Card,
  Hero,
  Hire,
  Plan,
  SectionBlock,
  text,
} from '@toggl/ui'
import HeaderAnnouncement from 'components/HeaderAnnouncement'
import { LayoutGlobal } from 'components/layouts'

import typeof { HeaderAnnouncementYaml } from '../components/HeaderAnnouncement/data.yml'

import typeof { ShoutOuts } from './index.yml'

declare var globalThis: any

const features = [
  {
    title: 'Record 25% more billable hours and improve profitability',
    paragraph:
      'No more guesstimates and lost billables. Track your time accurately, improve operational efficiency—and ultimately, maximize your profits.',
    image: require('./assets/illo-businesses.svg').default,
    alt:
      'Illustration of a few people sitting on a birdhouse with an increasing graph appearing from its window',
    background: `${colors.yellow60}`,
  },
  {
    title: 'Track from anywhere',
    paragraph:
      'Toggl Track integrates with 100+ tools. Connect your entire stack, and track time wherever you work—on the web app, on your browser, desktop, mobile or even your watch.',
    image: require('./assets/illo-desktopmobile.svg').default,
    alt: 'Icons of hands using a laptop and a mobile phone',
    background: `${colors.pink30}`,
  },
  {
    title:
      'Get employee buy-in with time tracking that puts privacy on priority',
    paragraph:
      'Get accurate data with an anti-surveillance time tracking solution that your employees are happy to use.',
    image: require('./assets/illo-privacy.svg').default,
    alt:
      'Illustration of a lock. The keyhole doubles as a head of a smiling person.',
    background: `${colors.peach80}`,
  },
]

const IndexPage = ({
  data,
}: {
  data: {
    headerAnnouncementYaml: HeaderAnnouncementYaml,
    indexYaml: { shoutOuts: ShoutOuts },
  },
}) => {
  const announcement = React.useMemo(() => {
    return data.headerAnnouncementYaml?.announcements.find((a) => a.active)
  }, [data])

  return (
    <LayoutGlobal
      announcement={
        announcement && <HeaderAnnouncement data={announcement.text} />
      }
      title={'Toggl: Time Tracking, Project Planning & Hiring Software'}
      description={
        'Stress-free time tracking software, project-planning, and hiring. Designed by and for teams that work from anywhere.'
      }
      ogImage={require('assets/toggl-global-main.jpg').default}
    >
      <ThemeProvider theme={themes.track}>
        <Hero.Wrapper>
          <HeroContentWrapper>
            <HeroContent>
              <HeroLeftContent>
                <HeroTitle>
                  Time tracking that's easy, powerful, <em>and</em>{' '}
                  frictionless.
                </HeroTitle>
                <HeroSubtitle color={colors.almostWhite}>
                  Time tracking software to boost performance and get paid for
                  every billable minute. An intuitive tool that makes time
                  tracking painless for the entire team.
                </HeroSubtitle>

                <ButtonWrapper>
                  <Button.Primary as={Link} to="/track/signup/">
                    Start tracking for free
                  </Button.Primary>
                  <Button.Secondary
                    as={Link}
                    to="/track/"
                    caretDirection="right"
                    color={colors.almostWhite}
                  >
                    Or explore Toggl Track
                  </Button.Secondary>
                </ButtonWrapper>
              </HeroLeftContent>
              <HeroRightContent>
                <StaticImage
                  formats={['auto', 'webp', 'avif']}
                  src={'./assets/screen-track-app.png'}
                  alt={
                    'Screenshot of the Toggl Track web app showcasing its benefits such as simple time tracking, reporting for billables and team time.'
                  }
                  width={550}
                  placeholder="none"
                  objectFit="contain"
                />
              </HeroRightContent>
            </HeroContent>

            <SocialProof background={colors.darkPurple}>
              <text.UppercaseOneLiner color={colors.darkPurple20}>
                Trusted by over 5 million users across 120 countries
              </text.UppercaseOneLiner>
              <Brands
                image={
                  <StaticImage
                    src="./assets/brands-mixed-desktop-light.png"
                    alt="Companies that use Toggl Track: Amazon, Uber, GE, LinkedIn, SAP, Danfoss, Wise, Ogilvy, Booking.com"
                    width={1120}
                    formats={['auto', 'webp', 'avif']}
                    placeholder="none"
                    objectFit="contain"
                  />
                }
                mobileImage={
                  <StaticImage
                    src="./assets/brands-mixed-mobile-light.png"
                    alt="Companies that use Toggl Track: Amazon, Uber, GE, LinkedIn, SAP, Danfoss, Wise, Ogilvy"
                    width={450}
                    formats={['auto', 'webp', 'avif']}
                    placeholder="none"
                    objectFit="contain"
                  />
                }
              />
            </SocialProof>
          </HeroContentWrapper>
        </Hero.Wrapper>

        <SectionBlock background={colors.peach20}>
          <WhyTrackTitle>
            <text.H2 color={colors.fadedPurple}>
              Time tracking is good for business. <ResponsiveBR></ResponsiveBR>
              But it’s also a <text.Rotalic>dreaded</text.Rotalic> chore.
            </text.H2>
            <text.P1 color={colors.darkPurple70}>
              Skip the timesheets and get your team an intuitive time tracker
              that gets the job done for you.
            </text.P1>
            <text.H3 color={colors.fadedPurple}>
              Here's why teams{' '}
              <text.Rotalic color={colors.pink}>love </text.Rotalic>Track:
            </text.H3>
          </WhyTrackTitle>

          <AnyoneCanTrackFeatures>
            {features.map((feature, index) => (
              <Card.FeatureIcon
                key={index}
                image={
                  <img src={feature.image} alt={feature.alt} loading="lazy" />
                }
                title={feature.title}
                paragraph={feature.paragraph}
                padding
                background={feature.background}
              />
            ))}
          </AnyoneCanTrackFeatures>

          <WhyTrackCTA>
            <StaticImage
              src="./assets/badges-track.png"
              alt="Badges Toggl Track has earned, including Top 100 Software Products"
              width={500}
              formats={['auto', 'webp', 'avif']}
              placeholder="none"
              objectFit="contain"
            />
            <text.H3 color={colors.fadedPurple}>
              5 million+ users have said yes to Track
            </text.H3>
            <ButtonWrapper>
              <Button.Primary as={Link} to="/track/signup/">
                Start tracking for free
              </Button.Primary>
              <Button.Secondary
                as={Link}
                to="/track/pricing/"
                caretDirection="right"
                color={colors.fadedPurpleAlt}
              >
                Or explore pricing plans
              </Button.Secondary>
            </ButtonWrapper>
          </WhyTrackCTA>
        </SectionBlock>
      </ThemeProvider>

      <TrackUseCase />

      <SectionBlock background={colors.darkPurple}>
        <SectionTitle color={colors.almostWhite}>
          Toggl builds tools to make your work life more efficient. Toggl Track
          is our time tracker.
          <br />
          <br />
          <text.Rotalic color={colors.yellow}>But we have more.</text.Rotalic>
        </SectionTitle>
        <ThemeProvider theme={themes.plan}>
          <ProductCard>
            <ProductContentGrid>
              <ProductText>
                <ProductSubText>
                  Want a simple tool to manage all your products in one place?
                </ProductSubText>
                <text.H2 color={colors.darkPurple}>
                  <text.Rotalic>Effortless</text.Rotalic> work planning for
                  teams with <Plan />
                </text.H2>
                <text.P2 color={colors.fadedPurpleAlt}>
                  Visually plan, track, and deliver work projects without the
                  last-minute rush and stress. With Toggl Plan's delightfully
                  simple drag-and-drop timelines, you can:
                </text.P2>

                <ProductPointIcon>
                  <StaticImage
                    src="./assets/icon-thumbs-up.png"
                    alt="Icon of a thumbs up"
                    width={65}
                    formats={['auto', 'webp', 'avif']}
                    placeholder="none"
                    objectFit="contain"
                  />
                  <text.P2 color={colors.fadedPurpleAlt}>
                    Create <text.Strong>effective work schedules</text.Strong>{' '}
                    without clunky spreadsheets or complex software
                  </text.P2>
                </ProductPointIcon>

                <ProductPointIcon>
                  <StaticImage
                    src="./assets/icon-done.png"
                    alt="Icon of a fire with a checkmark"
                    width={65}
                    formats={['auto', 'webp', 'avif']}
                    placeholder="none"
                    objectFit="contain"
                  />
                  <text.P2 color={colors.fadedPurpleAlt}>
                    Stay on schedule and{' '}
                    <text.Strong>get tasks done</text.Strong> by deadlines
                  </text.P2>
                </ProductPointIcon>

                <ProductPointIcon>
                  <StaticImage
                    src="./assets/icon-eye.png"
                    alt="Icon of an eye"
                    width={70}
                    formats={['auto', 'webp', 'avif']}
                    placeholder="none"
                    objectFit="contain"
                  />
                  <text.P2 color={colors.fadedPurpleAlt}>
                    <text.Strong>Get visibility</text.Strong> into your team's
                    availability and workload
                  </text.P2>
                </ProductPointIcon>
              </ProductText>
              <ProductImage>
                <StaticImage
                  src="./assets/screen-plan.png"
                  alt="Screenshot of the Toggl Plan web app"
                  width={680}
                  formats={['auto', 'webp', 'avif']}
                  placeholder="none"
                  objectFit="contain"
                />
                <ProductTestimonial>
                  <text.H5 color={colors.fadedPurple}>
                    Using spreadsheets to manage multiple projects was a
                    disaster. With Toggl Plan, we can easily keep track of ten
                    or more projects at a time.
                  </text.H5>
                  <a href={withPrefix('/plan/customer-stories/buzzfeed')}>
                    Chanel Kennedy, Manager at Boiler Room
                  </a>
                </ProductTestimonial>
              </ProductImage>
            </ProductContentGrid>
            <ProductCTA>
              <text.H3 color={colors.darkPurple}>
                See what better work planning looks like:
              </text.H3>
              <Button.Primary as="a" href={withPrefix('/plan')}>
                Start planning for free
              </Button.Primary>
            </ProductCTA>
          </ProductCard>
        </ThemeProvider>

        <ThemeProvider theme={themes.hire}>
          <ProductCardHire>
            <ProductContentGrid>
              <ProductText>
                <ProductSubText>
                  Is hiring eating up your time? <ResponsiveBR></ResponsiveBR>
                  There’s a better way.
                </ProductSubText>
                <text.H2 color={colors.darkPurple}>
                  Find your <text.Rotalic>best-fit</text.Rotalic> candidates
                  with <Hire />
                </text.H2>
                <text.P2 color={colors.fadedPurpleAlt}>
                  For the longest time, Toggl Hire was our unfair advantage in
                  effortlessly assessing, shortlisting and hiring incredible
                  people around the world. Now we made it available to everyone.
                </text.P2>

                <ProductPointIcon>
                  <StaticImage
                    src="./assets/icon-time.png"
                    alt="Icon of a stopwatch"
                    width={65}
                    formats={['auto', 'webp', 'avif']}
                    placeholder="none"
                    objectFit="contain"
                  />
                  <text.P2 color={colors.fadedPurpleAlt}>
                    <text.Strong>
                      Put candidate screening on autopilot:
                    </text.Strong>{' '}
                    quickly narrow down your candidate list to top performers
                    with job-relevant skills tests.
                  </text.P2>
                </ProductPointIcon>

                <ProductPointIcon>
                  <StaticImage
                    src="./assets/icon-tests.png"
                    alt="Icon of several papers with a plus sign"
                    width={65}
                    formats={['auto', 'webp', 'avif']}
                    placeholder="none"
                    objectFit="contain"
                  />
                  <text.P2 color={colors.fadedPurpleAlt}>
                    <text.Strong>
                      Stack several assessments into a role-specific hiring
                      funnel:
                    </text.Strong>{' '}
                    make data-driven hiring decisions at every step.
                  </text.P2>
                </ProductPointIcon>

                <ProductPointIcon>
                  <StaticImage
                    src="./assets/icon-prize.png"
                    alt="Icon of a trophy"
                    width={70}
                    formats={['auto', 'webp', 'avif']}
                    placeholder="none"
                    objectFit="contain"
                  />
                  <text.P2 color={colors.fadedPurpleAlt}>
                    <text.Strong>Assess any skill for any role:</text.Strong>{' '}
                    pick from 100+ ready-made test templates or create your own
                    custom assessments.
                  </text.P2>
                </ProductPointIcon>
              </ProductText>
              <ProductImage>
                <StaticImage
                  src="./assets/screen-hire.png"
                  alt="Screenshot of the Toggl Hire web app"
                  width={680}
                  formats={['auto', 'webp', 'avif']}
                  placeholder="none"
                  objectFit="contain"
                />
                <ProductTestimonial>
                  <text.H5 color={colors.fadedPurple}>
                    Our hiring process involves less manual work. Toggl Hire
                    gives a pretty good first idea of the candidate’s skill
                    level and knowledge. This saves our recruiters lots of time!
                  </text.H5>
                  <a
                    href={withPrefix(
                      '/hire/customer-story/speeding-up-moneses-hiring-process'
                    )}
                  >
                    Triinu, HR Manager at Monese
                  </a>
                </ProductTestimonial>
              </ProductImage>
            </ProductContentGrid>
            <ProductCTA>
              <text.H3 color={colors.darkPurple}>
                See what time-efficient hiring looks like:
              </text.H3>
              <Button.Primary as="a" href={withPrefix('/hire')}>
                Start hiring for free
              </Button.Primary>
            </ProductCTA>
          </ProductCardHire>
        </ThemeProvider>
      </SectionBlock>

      <SectionBlock background={colors.peach30}>
        <SectionTitle color={colors.fadedPurple}>
          Run your business at full efficiency.
          <ResponsiveBR></ResponsiveBR> Learn with Toggl.
        </SectionTitle>
        <SectionSubtitle color={colors.darkPurple70}>
          While there's no one right way to do things, there are many
          tried-and-tested ways. Check out our{' '}
          <Link to="/track/productivity-resources/">resources</Link> and our{' '}
          <a href="https://toggl.com/blog/">blog</a> for tips and tricks on
          better work.
        </SectionSubtitle>
        <ResourceItems>
          <ResourceCard
            flipped
            subtitle="Managing growth, increasing growth, hiring, and more — we talk about them all!"
            title="The Toggl Blog"
            link="/blog/"
            buttonText="Visit blog"
            image={
              <StaticImage
                formats={['auto', 'webp', 'avif']}
                src={'./assets/resource-blog-b.jpg'}
                alt={'The Toggl Blog logo'}
                width={360}
                height={180}
                placeholder="none"
              />
            }
          />
          <ResourceCard
            flipped
            subtitle="From increasing project profitability to managing team resources, see how users use Toggl Track for their business"
            title="Toggl Track Case Studies"
            link="/track/customers/"
            buttonText="Read case studies"
            image={
              <StaticImage
                src="../assets/cs-team-fullstacklabs.jpg"
                alt="Photo of FullStack Labs' office"
                width={360}
                height={180}
                sizes="(max-width: 525px) 450px, 900px"
                formats={['auto', 'webp', 'avif']}
                placeholder="none"
                objectFit="cover"
              />
            }
          />
          <ResourceCard
            flipped
            subtitle="Try these resources that we've gathered throughout our years in the productivity space"
            title="Track's Productivity Resources"
            link="/track/productivity-resources/"
            buttonText="Check it out"
            image={
              <StaticImage
                width={360}
                height={180}
                sizes="(max-width: 525px) 450px, 900px"
                formats={['auto', 'webp', 'avif']}
                src="../assets/resource-timesheet-templates.jpg"
                alt={'Repeating background of the letter "T"'}
                placeholder="none"
                objectFit="cover"
              />
            }
          />
          <ResourceCard
            noButton
            noChange
            title="Work From Home Hub"
            subtitle="With the right resources, working from home can be an enriching experience"
            link="/work-from-home/"
            image={
              <StaticImage
                formats={['auto', 'webp', 'avif']}
                src={'../assets/resource-wfh-hub.jpg'}
                alt={'Illustration of someone eating at home while working'}
                width={120}
                placeholder="none"
              />
            }
          />
          <ResourceCard
            noButton
            noChange
            subtitle="A practical guide to hiring remote teams: Learn what you can do to make better remote hiring decisions"
            title="Hiring Remotely"
            link="https://ebook.hire.toggl.com/"
            image={
              <StaticImage
                formats={['auto', 'webp', 'avif']}
                src="./assets/resource-hiring-remotely.jpg"
                alt={'Hiring Remotely Online Guide'}
                width={120}
                placeholder="none"
              />
            }
          />
        </ResourceItems>
      </SectionBlock>

      <MissionSectionWrapper background={colors.fadedPurple}>
        <MissionSection>
          <div>
            <text.H2 color={colors.almostWhite}>
              Everyone works differently.
            </text.H2>
            <text.P1 color={colors.darkPurple20}>
              That's why our fully-remote team is committed to flexible work and
              to building tools that fit into any workflow.
            </text.P1>
            <Button.Primary as={Link} to="/mission/">
              Read about Toggl's mission
            </Button.Primary>
          </div>
          <div>
            <StaticImage
              formats={['auto', 'webp', 'avif']}
              src={'./assets/illo-strength.png'}
              alt={
                'Illustration of a woman holding blocks that are shaped like arms'
              }
              width={500}
              placeholder="none"
              objectFit="contain"
            />
          </div>
        </MissionSection>
      </MissionSectionWrapper>
    </LayoutGlobal>
  )
}

const TrackUseCase = () => {
  const [selectedTab, selectTab] = React.useState<
    'employee' | 'billing' | 'project'
  >('employee')
  const activateEmployee = React.useCallback(() => selectTab('employee'), [])
  const activateBilling = React.useCallback(() => selectTab('billing'), [])
  const activateProject = React.useCallback(() => selectTab('project'), [])

  return (
    <SectionBlock background={colors.peach50}>
      <text.H2 color={colors.fadedPurple}>
        Time tracking software for all your <br /> productivity and
        profitability needs
      </text.H2>
      <ThemeProvider theme={themes.track}>
        <Tabs>
          <Tab active={selectedTab === 'employee'} onClick={activateEmployee}>
            Employee time tracking
          </Tab>
          <Tab active={selectedTab === 'billing'} onClick={activateBilling}>
            Billing & invoicing
          </Tab>
          <Tab active={selectedTab === 'project'} onClick={activateProject}>
            Project budgeting
          </Tab>
          <Button.Secondary
            as={Link}
            to="/track/#use-cases"
            caretDirection="right"
            color={colors.fadedPurpleAlt}
          >
            See more use cases
          </Button.Secondary>
        </Tabs>

        {selectedTab === 'employee' && (
          <TabItem>
            <TabItemText>
              <text.H3 color={colors.fadedPurple}>
                Boost accountability <ResponsiveBR></ResponsiveBR>across teams
              </text.H3>
              <text.P2 color={colors.darkPurple70}>
                Stay informed, accountable, and transparent. Make payroll and
                quarterly reporting easy with intuitive time tracking and
                accurate time reports for growing teams.
              </text.P2>
              <Button.Secondary
                as={Link}
                to="/track/employee-time-tracking/"
                caretDirection="right"
                color={colors.pink80}
              >
                Learn more about employee time tracking
              </Button.Secondary>
            </TabItemText>

            <TabImage>
              <StaticImage
                formats={['auto', 'webp', 'avif']}
                src={'./assets/employee-time-tracking.png'}
                alt={
                  'Screenshot of Toggl Track report that shows employee time tracked by team member and team'
                }
                height={600}
                placeholder="none"
                objectFit="contain"
              />
            </TabImage>
            <Quote>
              <text.P2 color={colors.fadedPurple}>
                "Toggl Track offers full control to our team members to
                accurately track their time. It is extremely easy and intuitive,
                as well as non-invasive."
                <Link to="/track/customers/newlogic/">
                  <text.Strong>Newlogic, Software Consultancy</text.Strong>
                </Link>
              </text.P2>
            </Quote>
          </TabItem>
        )}

        {selectedTab === 'billing' && (
          <TabItem>
            <TabItemText>
              <text.H3 color={colors.fadedPurple}>
                Record every billable hour
              </text.H3>
              <text.P2 color={colors.darkPurple70}>
                Users report a 25% increase in billable hours with Toggl Track.
                Leave behind messy spreadsheets and outdated timesheets for
                simpler time entry, improved profitability, and accurate
                invoices for your clients.
              </text.P2>
              <Button.Secondary
                as={Link}
                to="/track/time-tracking-invoicing/"
                caretDirection="right"
                color={colors.pink80}
              >
                Learn more about billing & invoicing
              </Button.Secondary>
            </TabItemText>

            <TabImage>
              <StaticImage
                formats={['auto', 'webp', 'avif']}
                src={'./assets/billing-invoicing.png'}
                alt={
                  'Screenshot of Toggl Track report that shows time tracked by client'
                }
                height={600}
                placeholder="none"
                objectFit="contain"
              />
            </TabImage>
            <Quote>
              <text.P2 color={colors.fadedPurple}>
                "Toggl Track increased our profitability by at least 20%. We
                found out where the team was spending too much time on clients."
                <Link to="/track/customers/mister-sweat/">
                  <text.Strong>Sweat+Co, Public Relations Agency</text.Strong>
                </Link>
              </text.P2>
            </Quote>
          </TabItem>
        )}

        {selectedTab === 'project' && (
          <TabItem>
            <TabItemText>
              <text.H3 color={colors.fadedPurple}>
                Ensure that projects are profitable and employees aren’t burned
                out
              </text.H3>
              <text.P2 color={colors.darkPurple70}>
                Create project scopes and manage resources with ease. Track
                project health and assess project profitability and resource
                capacity in real time.
              </text.P2>
              <Button.Secondary
                as={Link}
                to="/track/project-time-tracking/"
                caretDirection="right"
                color={colors.pink80}
              >
                Learn more about project budgeting
              </Button.Secondary>
            </TabItemText>

            <TabImage>
              <StaticImage
                formats={['auto', 'webp', 'avif']}
                src={'./assets/project-budgeting.png'}
                alt={
                  'Screenshot of Toggl Track that shows project profitability'
                }
                height={600}
                placeholder="none"
                objectFit="contain"
              />
            </TabImage>
            <Quote>
              <text.P2 color={colors.fadedPurple}>
                "Toggl Track data provides a clear overview of project times and
                budget—a resource we can draw on for future projects."
                <Link to="/track/customers/impekable/">
                  <text.Strong>Impekable, Design Agency</text.Strong>
                </Link>
              </text.P2>
            </Quote>
          </TabItem>
        )}
      </ThemeProvider>
    </SectionBlock>
  )
}

export const query = graphql`
  {
    headerAnnouncementYaml {
      announcements {
        active
        text {
          body
        }
      }
    }
    indexYaml {
      shoutOuts {
        track {
          text
          url
          active
        }
        plan {
          text
          url
          active
        }
        hire {
          text
          url
          active
        }
      }
    }
  }
`

const ButtonWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  margin: 30px 0 10px;
  column-gap: 30px;
  row-gap: 20px;
  a {
    display: inline-flex;
  }
`

const ProductCard = styled.div`
  border-radius: 10px;
  padding: 30px;
  position: relative;
  background: ${colors.violet20};
  overflow: hidden;
  margin: 30px 0;
  &::before {
    content: url(${require('./assets/shape-half-triangle.png').default});
    position: absolute;
    top: 0;
    right: 100px;
    z-index: 0;
  }
  ${media.mq[1]} {
    padding: 45px;
  }
`

const ProductContentGrid = styled.div`
  position: relative;
  ${media.mq[2]} {
    display: flex;
    gap: 40px;
  }
`

const ProductCTA = styled(ButtonWrapper)`
  text-align: center;
  margin-top: 45px;
  justify-content: center;
`

const ProductText = styled.div`
  flex: 1;
  svg {
    vertical-align: middle;
    height: 28px;
    ${media.mq[1]} {
      height: 38px;
    }
    ${media.mq[2]} {
      height: 45px;
    }
  }
  h2 {
    font-size: 30px;
    margin-bottom: 20px;
    ${media.mq[1]} {
      font-size: 40px;
      margin-top: 40px;
      margin-bottom: 35px;
    }
    ${media.mq[2]} {
      font-size: 48px;
    }
  }
  p {
  }
`
const ProductSubText = styled(text.P3)`
  text-transform: uppercase;
  font-weight: bold;
  color: ${(p) => p.theme.primary};
  margin-bottom: 20px;
  ${media.mq[2]} {
    max-width: 380px;
  }
`

const ProductPointIcon = styled.div`
  display: flex;
  align-items: center;
  column-gap: 20px;
  margin: 25px 0;
  p {
    flex: 1;
  }
`

const ProductImage = styled.div`
  text-align: center;
  .gatsby-image-wrapper {
    z-index: 2;
  }
  ${media.mq[2]} {
    flex: 1;
    transform: translate(5%, -2%);
    .gatsby-image-wrapper {
      width: 120%;
    }
  }
`

const ProductCardHire = styled(ProductCard)`
  background: ${colors.orange10};
  margin-bottom: 0;
  &::before {
    content: url(${require('./assets/shape-half-moon.png').default});
    position: absolute;
    top: 45%;
    right: -50px;
  }
  h5 {
    margin-top: 45px !important;
  }
  ${media.mq[2]} {
    ${ProductImage} {
      transform: translate(5%, 5%);
    }
  }
`

const SocialProof = styled.div`
  margin-bottom: 30px;
  text-align: center;
  .gatsby-image-wrapper {
    width: 80%;
  }
  ${media.mq[1]} {
    text-align: left;
    margin: 35px 0 0;
    > div {
      margin: 0;
    }
    .gatsby-image-wrapper {
      width: 90%;
    }
  }
  p {
    margin-bottom: 20px;
  }
`

const Tabs = styled.div`
  margin: 30px 0;
  overflow-x: auto;
  display: flex;
  align-items: center;
  column-gap: 12px;
  width: calc(100% + 30px);
  padding: 0 30px 5px 0;
  ${media.mq[2]} {
    width: 100%;
  }
`

const Tab = styled.button`
  display: inline-block;
  ${text.defaultText};
  border: none;
  border-radius: 100px;
  background: ${(p) => (p.active ? colors.fadedPurple : colors.yellow60)};
  color: ${(p) => (p.active ? colors.yellow : colors.fadedPurple)};
  ${(p) => p.active && `font-family: ${fonts.gtHaptikMediumRotalic}`};
  padding: 13px 20px;
  margin: 5px 0;
  cursor: pointer;
  min-width: fit-content;
  + a {
    display: inline-flex;
    margin-left: 20px;
    min-width: fit-content;
  }
  &:hover {
    background: ${(p) => (p.active ? colors.fadedPurple : colors.yellow80)};
    font-family: ${fonts.gtHaptikMediumRotalic};
  }
`

const TabItem = styled.div`
  ${media.mq[1]} {
    display: grid;
    grid-template-columns: 1fr 1.5fr;
    align-items: center;
  }
`

const TabItemText = styled.div`
  padding-right: 30px;
  h3,
  p {
    margin: 20px 0;
  }
`

const Quote = styled.div`
  border-left: 3px solid ${colors.fadedPurple};
  padding: 0 30px;
  max-width: 650px;
  ${media.mq[1]} {
    grid-column: 1 / -1;
    grid-row: 2 / 3;
    margin: 45px 0 30px;
  }
  ${media.mq[2]} {
    grid-column: 1 / 2;
  }
  a {
    ${text.paragraph3};
    color: ${colors.darkPurple70};
    text-decoration: underline;
    display: block;
    margin-top: 10px;
    &:hover {
      color: ${(p) => p.theme.primary};
    }
  }
`

const TabImage = styled.div`
  text-align: center;
  margin: 30px auto;
  ${media.mq[1]} {
    grid-column: 2 / 3;
    grid-row: 1 / 2;
    margin: auto;
  }
  ${media.mq[2]} {
    grid-column: 2 / 3;
    grid-row: 1 / 3;
  }
  .gatsby-image-wrapper {
    max-width: 90%;
    margin: auto;
  }
`

const MissionSectionWrapper = styled(SectionBlock)`
  padding: 60px 30px 0;
  ${media.mq[1]} {
    padding: 45px 30px 0;
  }
  ${media.mq[2]} {
    padding: 30px 30px 0;
  }
`

const MissionSection = styled.div`
  display: flex;
  flex-direction: column;
  text-align: center;
  ${media.mq[1]} {
    flex-direction: row;
    align-items: center;
    gap: 60px;
    text-align: left;
  }
  > div:nth-of-type(1) {
    ${media.mq[1]} {
      flex: 1 1 70%;
      padding-bottom: 40px;
    }
    ${media.mq[2]} {
      flex: 1 1 45%;
    }
  }
  > div:nth-of-type(2) {
    ${media.mq[1]} {
      align-self: end;
    }
  }
  p {
    margin: 15px 0 30px;
  }
  a {
    display: inline-flex;
  }
  .gatsby-image-wrapper {
    height: 100%;
  }
`

const HeroContentWrapper = styled(Hero.ContentWrapper)`
  ${media.mq[2]} {
    margin-top: 180px;
  }
`

const HeroContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
  ${media.mq[1]} {
    flex-direction: row;
  }
`

const HeroLeftContent = styled.div`
  flex: 1;
`

const HeroRightContent = styled.div`
  ${media.mq[1]} {
    flex: 0 0 auto;
    max-width: 45%;
  }
  ${media.mq[2]} {
    margin-top: -25px;
  }
  .gatsby-image-wrapper {
    width: 100%;
  }
`

const HeroTitle = styled(Hero.Title)`
  em {
    color: ${colors.almostWhite};
  }
  ${media.mq[2]} {
    margin-top: 20px;
  }
`

const HeroSubtitle = styled(text.P2)`
  max-width: 90%;
`

const SectionTitle = styled(text.H2)`
  text-align: center;
  max-width: 850px;
  margin: 0 auto 60px;
`

const SectionSubtitle = styled(text.P2)`
  margin: -30px auto 60px;
  max-width: 700px;
  text-align: center;
  a {
    text-decoration: underline;
    color: ${colors.darkPurple70};
    &:hover {
      color: ${colors.darkPurple};
    }
  }
`

const ResponsiveBR = styled.br`
  display: none;
  ${media.mq[2]} {
    display: block;
  }
`

const ProductTestimonial = styled.div`
  position: relative;
  &:before {
    content: '';
    position: absolute;
    width: 380px;
    height: 380px;
    bottom: -20%;
    left: 0;
    right: 0;
    margin-left: auto;
    margin-right: auto;
    opacity: 0.1;
    border-radius: 100%;
    background: ${(p) => p.theme.primary};
    z-index: 1;
  }
  h5 {
    max-width: 63%;
    text-align: center;
    margin: 55px auto 20px;
    font-size: clamp(1.25rem * 0.8, 1.25vw, 1.25rem * 1);
    position: relative;
    &:before {
      content: '“';
      color: ${(p) => p.theme.primary};
      font-size: 55px;
      position: absolute;
      transform: translate(-35px, -20px);
      line-height: 1;
    }
  }
  a {
    text-decoration: underline;
    ${text.paragraph4};
    color: ${colors.darkPurple70};
    z-index: 2;
    position: relative;
    &:hover {
      color: ${(p) => p.theme.primary};
    }
  }
`
const WhyTrackTitle = styled.div`
  text-align: center;
  max-width: 850px;
  margin: 30px auto;
  h3 {
    margin: 45px auto 20px;
    ${media.mq[1]} {
      margin: 60px auto 30px;
    }
  }
  p {
    max-width: 600px;
    margin: 30px auto;
  }
`

const AnyoneCanTrackFeatures = styled.div`
  margin: 30px 0;
  ${media.mq[1]} {
    display: flex;
    column-gap: 30px;
  }
  ${media.mq[2]} {
    column-gap: 40px;
  }
`

const WhyTrackCTA = styled.div`
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 30px;
  margin: 60px auto 15px;
  ${ButtonWrapper} {
    margin-top: 0;
  }
`

const ResourceItems = styled.div`
  ${media.mq[1]} {
    display: grid;
    grid-template-columns: repeat(6, 1fr);
    column-gap: 30px;
    row-gap: 40px;
  }
  ${media.mq[2]} {
    row-gap: 60px;
  }
`

const ResourceCard = styled(Card.CaseStudy)`
  grid-column: span 3;
  &:hover {
    h4,
    button {
      color: ${colors.darkPurple70};
      font-family: ${fonts.gtHaptikMediumRotalic};
    }
  }
  ${media.mq[1]} {
    margin: 0;
  }
  ${media.mq[1]} {
    &:nth-of-type(-n + 3) {
      grid-column: span 2;
    }
  }
  h4 {
    color: ${colors.darkPurple};
  }
  p {
    ${text.paragraph3};
    color: ${colors.darkPurple70};
    margin-bottom: 15px;
  }
`

export default IndexPage
